import { Renderer } from '../render/renderer'
import { heroSpriteName } from '../render/pixelart'
import { iconHTML, itemIconSprite, tomeIconSprite } from '../render/icon'
import { rotateIntent } from '../render/camerafx'
import { TouchControls, isTouchDevice } from '../ui/touchcontrols'
import { Input, PAD } from '../core/input'
import { AudioManager } from '../audio/audio'
import { UI, ShopOffer } from '../ui/ui'
import { World, allocId } from '../sim/world'
import { stepWorld } from '../sim/update'
import { healPlayer } from '../sim/combat'
import { Intent, CLIFF_SLOPE } from '../sim/systems'
import { gentleSpot, plateauSpot, basinSpot, structureOffsets, landmarkOffsets } from '../core/placement'
import { biomeSetPiece, frameRing } from '../data/setpieces'
import { propFootprint, bodyRadius } from '../sim/footprint'
import { recomputeStats } from '../sim/stats'
import { generateOptions, buildCandidates, UpgradeOption, optionKey, isBanishable, sourceAllows, LootSource, MAX_WEAPONS, MAX_ITEMS, MAX_TOMES } from '../data/upgrades'
import { RARITY_ORDER, Rarity } from '../sim/types'
import { CHARACTERS } from '../data/characters'
import { MAPS, MAP_LIST, DIFFICULTY_MAP, pickStageGoal } from '../data/maps'
import {
  computeMetaBonuses,
  loadMeta,
  MetaState,
  resetMeta,
  saveMeta,
  upgradeCost,
  META_UPGRADE_MAP,
} from '../meta/meta'
import { CHARACTER_LIST } from '../data/characters'
import { evaluateAchievements } from '../data/achievements'
import { ITEMS, ITEM_LIST } from '../data/items'
import { ENEMIES } from '../data/enemies'
import { TOMES } from '../data/tomes'
import { CHALLENGES } from '../data/challenges'
import { t, tName, toggleLang, bossTitle } from '../i18n'

type AppState = 'menu' | 'charselect' | 'mapselect' | 'shop' | 'run'

export class Game {
  private renderer: Renderer
  private input = new Input()
  private audio = new AudioManager()
  private ui: UI
  private touch: TouchControls | null = null
  private meta: MetaState

  private app: AppState = 'menu'
  private world: World | null = null
  private metaBonuses = computeMetaBonuses({ upgrades: {} } as MetaState)

  private selectedChar = 'bonker'
  private selectedMap = 'meadow'
  private selectedDifficulty = 'normal'
  private selectedChallenge = ''
  private selectedTier = 1
  private last = 0
  private fps = 60
  private levelUpShown = false
  private victoryShown = false
  private chestQueue = 0
  private bossChestQueue = 0 // how many queued chests are BOSS chests (keep the rarest-reward source when drained late)
  private hitStopAcc = 0 // carries the sub-FIXED hit-stop time so the sim only advances in whole deterministic ticks
  private pendingJump = false // edge-triggered jump latched across hit-stop frames that ran no tick (so it isn't eaten)
  private pendingSlide = false // edge-triggered slide/dodge latched the same way
  private crunchCd = 0 // throttle timer for the combat screen-shake rattle (s) — keeps shake punctual under a dense kill stream
  private prevCombo = 0 // last frame's kill-combo, for render-only milestone floaters (25/50/100/200 COMBO!)
  private unlockCheckAt = 0 // next world.time to re-check mid-run unlocks (fire the "UNLOCKED!" toast the instant a threshold is crossed)
  private wonPending = false // a run-win is pending behind a boss reward chest (show victory once it's taken)
  private currentOptions: UpgradeOption[] = []
  private rerollCount = 0
  private shopRerollCount = 0
  private shopOffers: ShopOffer[] = []
  private prevHp = 0
  private prevKills = 0
  private seedCounter = 1

  constructor(root: HTMLElement) {
    this.renderer = new Renderer(root)
    this.ui = new UI(root)
    this.meta = loadMeta()
    this.audio.masterVol = this.meta.settings.master
    this.audio.sfxVol = this.meta.settings.sfx
    this.audio.musicVol = this.meta.settings.music
    this.input.attach()
    if (isTouchDevice()) this.touch = new TouchControls(root, this.input)
    // backgrounding the tab: silence audio (the ambient bed runs on the audio thread, not rAF) and auto-pause a run
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.audio.suspend()
        if (this.app === 'run' && this.world && this.world.phase === 'playing') { this.world.phase = 'paused'; this.showPause(this.world) }
      } else {
        this.audio.resume()
      }
    })
    this.toMenu()
    requestAnimationFrame(this.loop)
  }

  // ---- navigation ----
  private toMenu(): void {
    this.app = 'menu'
    this.world = null
    this.audio.stopAmbience() // silence the biome bed on EVERY exit path (Pause→Quit didn't go through endRun)
    saveMeta(this.meta) // persist in-run meta mutations (e.g. evolvedTotal) when quitting without a clean endRun
    this.touch?.setActive(false)
    this.ui.hideHud()
    this.ui.mainMenu(this.meta, {
      play: () => {
        this.audio.ensure()
        this.audio.play('select')
        this.selectedChallenge = ''
        this.toCharSelect()
      },
      shop: () => {
        this.audio.ensure()
        this.toShop()
      },
      lang: () => {
        toggleLang()
        this.toMenu()
      },
      stats: () => this.toStats(),
      settings: () => this.toSettings(),
      achievements: () => this.toAchievements(),
      codex: () => this.ui.codexScreen({ close: () => this.toMenu() }),
      challenges: () => this.toChallenges(),
    })
  }

  private toChallenges(): void {
    this.ui.challengesScreen(this.meta, {
      pick: (id) => {
        this.selectedChallenge = id
        this.audio.play('select')
        this.toCharSelect()
      },
      back: () => this.toMenu(),
    })
  }

  private toStats(): void {
    this.ui.statsScreen(this.meta, { close: () => this.toMenu() })
  }

  private toAchievements(): void {
    this.ui.achievementsScreen(this.meta, { close: () => this.toMenu() })
  }

  private toSettings(): void {
    this.ui.settingsScreen(this.meta, {
      close: () => {
        saveMeta(this.meta)
        this.toMenu()
      },
      apply: () => {
        this.audio.masterVol = this.meta.settings.master
        this.audio.sfxVol = this.meta.settings.sfx
        this.audio.musicVol = this.meta.settings.music
        this.audio.applyVolumes()
        saveMeta(this.meta)
      },
      lang: () => {
        toggleLang()
        this.toSettings()
      },
    })
  }

  private toCharSelect(): void {
    this.app = 'charselect'
    this.ui.charSelect(this.meta, {
      pick: (id) => {
        this.selectedChar = id
        this.audio.play('select')
        this.toMapSelect()
      },
      back: () => this.toMenu(),
    })
  }

  private toMapSelect(): void {
    this.app = 'mapselect'
    this.ui.mapSelect(this.meta, {
      pick: (id) => {
        this.selectedMap = id
        this.audio.play('select')
        this.toTierSelect()
      },
      back: () => this.toCharSelect(),
    })
  }

  private toTierSelect(): void {
    this.app = 'mapselect'
    const map = MAPS[this.selectedMap]
    const best = this.meta.bestTier[this.selectedMap] ?? 0
    this.ui.tierSelect(this.selectedMap, map.maxTier ?? 3, best, {
      pick: (tier) => {
        this.selectedTier = tier
        this.audio.play('select')
        this.toDifficultySelect()
      },
      back: () => this.toMapSelect(),
    })
  }

  private toDifficultySelect(): void {
    this.app = 'mapselect'
    this.ui.difficultySelect({
      pick: (id) => {
        this.selectedDifficulty = id
        this.audio.play('select')
        this.startRun(this.selectedChar, this.selectedMap, id, this.selectedTier)
      },
      back: () => this.toTierSelect(),
    })
  }

  private toShop(): void {
    this.app = 'shop'
    this.ui.shop(this.meta, {
      buy: (id) => this.buyUpgrade(id),
      back: () => this.toMenu(),
      toggler: () => this.toToggler(),
      reset: () => {
        this.ui.confirm(t('resetConfirm'), t('resetYes'), () => {
          this.meta = resetMeta()
          this.toShop()
        })
      },
    })
  }

  private toToggler(): void {
    this.ui.togglerScreen(this.meta, {
      toggle: (id) => {
        const i = this.meta.disabledItems.indexOf(id)
        if (i >= 0) this.meta.disabledItems.splice(i, 1)
        else this.meta.disabledItems.push(id)
        saveMeta(this.meta)
        this.audio.play('select')
        this.toToggler() // re-render
      },
      back: () => this.toShop(),
    })
  }

  private buyUpgrade(id: string): void {
    const lvl = this.meta.upgrades[id] ?? 0
    const cost = upgradeCost(id, lvl)
    const def = META_UPGRADE_MAP[id]
    if (cost === null || !def || this.meta.bank < cost) return
    this.meta.bank -= cost
    this.meta.upgrades[id] = lvl + 1
    saveMeta(this.meta)
    this.audio.play('levelup')
    this.toShop()
  }

  // ---- run lifecycle ----
  private startRun(charId: string, mapId: string, difficultyId = 'normal', tier = 1, seedOverride?: number): void {
    const char = CHARACTERS[charId]
    const map = MAPS[mapId]
    const diff = DIFFICULTY_MAP[difficultyId] ?? DIFFICULTY_MAP.normal
    const tierDiffMul = 1 + (tier - 1) * 0.5 // tier scales difficulty + reward + stages
    this.metaBonuses = computeMetaBonuses(this.meta)
    const world = new World({
      // MP8: an explicit seed (shared code / daily run) reproduces the exact run; else auto-roll
      seed: seedOverride !== undefined ? seedOverride >>> 0 : (this.seedCounter++ * 2654435761) >>> 0,
      map,
      characterId: charId,
      difficulty: map.intensity * diff.mult * tierDiffMul,
      rewardMult: diff.rewardMult * (1 + (tier - 1) * 0.35),
    })
    world.tier = tier
    world.stages = tier
    world.stage = 0
    // MP6: pick this stage's pre-portal goal (meadow tier-1 stays the classic survive run)
    {
      const g = pickStageGoal(map.id, tier, 0, world.rng)
      world.stageGoal = g.goal
      world.goalTarget = g.target
      world.goalProgress = 0
    }
    // starting weapon + character passive + challenge modifier
    world.passive = char.passive ?? ''
    world.gimmick = char.gimmick ?? ''
    world.disabledItems = new Set(this.meta.disabledItems)
    world.challenge = this.selectedChallenge
    if (world.challenge) CHALLENGES[world.challenge]?.applyWorld?.(world)
    world.curse += (tier - 1) * 2 // higher tiers start cursed (tougher + better loot)
    world.weapons.push({ defId: char.startWeapon, level: 1, cooldown: 0, evolved: false, scratch: {} })
    recomputeStats(world, this.metaBonuses)
    world.player.hp = world.stats.maxHp
    const pois = this.genPOIs(world)
    this.scatterProps(world, pois)
    this.scatterObstacles(world, pois)
    // fixed Moai + microwave landmarks at seeded POIs (discover them, not teleport-spawned)
    if (pois[0]) world.shrines.push({ id: allocId(), x: pois[0].x, z: pois[0].z, type: 'moai', used: false })
    if (pois[1]) world.shrines.push({ id: allocId(), x: pois[1].x, z: pois[1].z, type: 'microwave', used: false })
    // frame each landmark with a small decorative marker ring so it reads as a deliberate site
    for (const c of [pois[0], pois[1]]) {
      if (!c) continue
      for (const m of frameRing(world.rng, world.map.id)) {
        let p = this.clampToArena(world, c.x + m.dx, c.z + m.dz, 2)
        if (world.terrainAmp > 0) {
          const g = gentleSpot(world.rng, world.terrainSeed, world.terrainAmp, p.x, p.z, CLIFF_SLOPE, 3, 2, world.terrainCfg)
          p = this.clampToArena(world, g.x, g.z, 2)
        }
        this.placeScenery(world, p.x, p.z, m.sprite, m.scale)
      }
    }
    this.clearSealedObstacles(world) // never let an obstacle seal a chest / breakable / landmark shrine

    this.world = world
    // DEV-only debug handle (compiled out of production builds) for in-browser testing
    if (import.meta.env.DEV) (window as unknown as { __world: World }).__world = world
    this.app = 'run'
    this.touch?.setActive(true)
    this.levelUpShown = false
    this.victoryShown = false
    this.chestQueue = 0
    this.chestsOpened = 0
    this.bossChestQueue = 0
    this.hitStopAcc = 0
    this.crunchCd = 0
    this.pendingJump = false
    this.pendingSlide = false
    this.prevCombo = 0
    this.unlockCheckAt = 0
    this.wonPending = false
    this.chestGambleCount = 0
    this.prevHp = world.player.hp
    this.prevKills = 0

    this.renderer.camYaw = 0 // reset the free-orbit camera so a new run starts behind the player (not the prior run's angle)
    this.renderer.setMap(map)
    this.renderer.setPlayerColor(char.color)
    this.renderer.setPlayerSprite(heroSpriteName(char.id))
    this.ui.hideOverlay()
    this.ui.showHud()
    this.audio.ensure()
    this.audio.setBiome(map.id)
    this.ui.toast(`${tName('char', char.id, char.name)} · ${tName('map', map.id, map.name)}`)
  }

  /** A normal-distributed offset (Box-Muller) for gaussian clustering. */
  private gauss(world: World): number {
    const u = Math.max(1e-6, world.rng.next())
    const v = world.rng.next()
    return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v)
  }

  /** Seeded points of interest the layout clusters around (ruins/yards/landmark). */
  private genPOIs(world: World): { x: number; z: number }[] {
    const r = world.map.radius - 8
    const n = 3 + world.rng.int(0, 2) // 3-5 POIs
    const pois: { x: number; z: number }[] = []
    for (let i = 0; i < n; i++) {
      const a = world.rng.next() * Math.PI * 2
      const dist = 14 + world.rng.next() * Math.max(4, r - 14)
      // settlements/ruins sit on buildable (low-slope) ground, not cliff faces
      const g = gentleSpot(world.rng, world.terrainSeed, world.terrainAmp, Math.cos(a) * dist, Math.sin(a) * dist, CLIFF_SLOPE * 0.6, 8, 4, world.terrainCfg)
      pois.push(this.clampToArena(world, g.x, g.z, 8))
    }
    return pois
  }

  private clampToArena(world: World, x: number, z: number, margin: number): { x: number; z: number } {
    const r = world.map.radius - margin
    const d = Math.hypot(x, z)
    if (d > r) return { x: (x / d) * r, z: (z / d) * r }
    return { x, z }
  }

  /**
   * CF5: place a piece of scenery, routing BIG set-piece/landmark props into solid obstacles (so they
   * block consistently with their identical-looking cover twins) and keeping small ones as pass-through
   * props. Position/sprite/scale are unchanged, so the seeded layout is identical — only solidity changes.
   */
  private placeScenery(world: World, x: number, z: number, sprite: string, scale: number): void {
    const fp = propFootprint(sprite, scale)
    if (fp.solid) world.obstacles.push({ x, z, radius: fp.radius, sprite, scale, blocksShots: fp.blocksShots })
    else world.props.push({ x, z, sprite, scale })
  }

  private scatterProps(world: World, pois: { x: number; z: number }[]): void {
    const byBiome: Record<string, string[]> = {
      meadow: ['prop:crater', 'prop:moonrock', 'prop:rock', 'prop:moonrock', 'prop:crater', 'prop:flag', 'prop:satellite'],
      dunes: ['prop:redrock', 'prop:redrock', 'prop:rock', 'prop:crater', 'prop:cactus', 'prop:moonrock', 'prop:antenna'],
      graveyard: ['prop:debris', 'prop:crate', 'prop:grave', 'prop:deadtree', 'prop:antenna', 'prop:debris', 'prop:pillar'],
      rift: ['prop:crystal', 'prop:crystal', 'prop:pillar', 'prop:debris', 'prop:rock'],
    }
    const list = byBiome[world.map.id] ?? ['prop:moonrock']
    // most props cluster around POIs (ruins), a few stay loose for texture
    for (let i = 0; i < 44; i++) {
      const clustered = world.rng.next() < 0.75 && pois.length > 0
      let x: number
      let z: number
      if (clustered) {
        const poi = pois[world.rng.int(0, pois.length - 1)]
        x = poi.x + this.gauss(world) * 4.5
        z = poi.z + this.gauss(world) * 4.5
      } else {
        const a = world.rng.next() * Math.PI * 2
        const dist = 9 + world.rng.next() * (world.map.radius - 13)
        x = Math.cos(a) * dist
        z = Math.sin(a) * dist
      }
      let p = this.clampToArena(world, x, z, 3)
      const sprite = list[world.rng.int(0, list.length - 1)]
      const tall = sprite === 'prop:antenna' || sprite === 'prop:flag' || sprite === 'prop:satellite'
      const scale = tall ? 2.4 + world.rng.next() * 0.8 : 1.6 + world.rng.next() * 1.0
      // tall structures need near-flat footing; everything avoids cliff faces (no floating/clipping)
      if (world.terrainAmp > 0) {
        const g = gentleSpot(world.rng, world.terrainSeed, world.terrainAmp, p.x, p.z, tall ? 0.22 : CLIFF_SLOPE, tall ? 8 : 4, 4, world.terrainCfg)
        p = this.clampToArena(world, g.x, g.z, 3)
      }
      this.placeScenery(world, p.x, p.z, sprite, scale)
    }
    // a seeded biome SET PIECE (boulder field / cactus arc / grave rows / crystal cluster) on a
    // mid POI so each world has a recognizable, deliberate-looking landmark from existing sprites
    const spCenter = pois[2] ?? pois[pois.length - 1] ?? { x: 0, z: 0 }
    for (const piece of biomeSetPiece(world.rng, world.map.id)) {
      let p = this.clampToArena(world, spCenter.x + piece.dx, spCenter.z + piece.dz, 3)
      if (world.terrainAmp > 0) {
        const g = gentleSpot(world.rng, world.terrainSeed, world.terrainAmp, p.x, p.z, CLIFF_SLOPE, 4, 3, world.terrainCfg)
        p = this.clampToArena(world, g.x, g.z, 3)
      }
      this.placeScenery(world, p.x, p.z, piece.sprite, piece.scale)
    }
    // ambient scatter: a thin layer of small ground-texture bits spread across the arena
    // (tiny, off-cliff, terrain-hugging) so the ground never reads as bare — cheap (batched)
    const ambient = byBiome[world.map.id]?.filter((s) => s === 'prop:rock' || s === 'prop:moonrock' || s === 'prop:redrock' || s === 'prop:debris' || s === 'prop:crater') ?? ['prop:moonrock']
    const ambList = ambient.length ? ambient : ['prop:moonrock']
    for (let i = 0; i < 30; i++) {
      const a = world.rng.next() * Math.PI * 2
      const dist = 7 + world.rng.next() * (world.map.radius - 10)
      let p = this.clampToArena(world, Math.cos(a) * dist, Math.sin(a) * dist, 2)
      if (world.terrainAmp > 0) {
        const g = gentleSpot(world.rng, world.terrainSeed, world.terrainAmp, p.x, p.z, CLIFF_SLOPE, 3, 3, world.terrainCfg)
        p = this.clampToArena(world, g.x, g.z, 2)
      }
      world.props.push({ x: p.x, z: p.z, sprite: ambList[world.rng.int(0, ambList.length - 1)], scale: 0.55 + world.rng.next() * 0.5 })
    }
    // breakables form a "yard" tightly clustered on the last POI + some near others
    const breaks = ['break:crate', 'break:pod', 'break:barrel']
    const yard = pois[pois.length - 1] ?? { x: 0, z: 0 }
    for (let i = 0; i < 26; i++) {
      const near = i < 14 ? yard : pois[world.rng.int(0, Math.max(0, pois.length - 1))] ?? yard
      let p = this.clampToArena(world, near.x + this.gauss(world) * 3.2, near.z + this.gauss(world) * 3.2, 4)
      // loot pools into basins/dips, and never clings to a cliff face
      if (world.terrainAmp > 0) {
        const b = basinSpot(world.rng, world.terrainSeed, world.terrainAmp, p.x, p.z, 4, world.terrainCfg)
        const g = gentleSpot(world.rng, world.terrainSeed, world.terrainAmp, b.x, b.z, CLIFF_SLOPE, 4, 4, world.terrainCfg)
        p = this.clampToArena(world, g.x, g.z, 4)
      }
      const bSprite = breaks[world.rng.int(0, breaks.length - 1)]
      const bScale = 1.5
      world.breakables.push({
        id: allocId(),
        x: p.x,
        z: p.z,
        hp: 1,
        maxHp: 1,
        radius: bodyRadius(bSprite, bScale), // CF6: hitbox tracks the drawn box body
        sprite: bSprite,
        scale: bScale,
        flash: 0,
      })
    }
    // a discoverable chest sits at each POI — drawn up onto a flat plateau as a climb-to landmark
    for (const poi of pois) {
      let p = this.clampToArena(world, poi.x, poi.z, 6)
      if (world.terrainAmp > 0) {
        const pl = plateauSpot(world.rng, world.terrainSeed, world.terrainAmp, p.x, p.z, 5, world.terrainCfg)
        const g = gentleSpot(world.rng, world.terrainSeed, world.terrainAmp, pl.x, pl.z, CLIFF_SLOPE * 0.7, 6, 4, world.terrainCfg)
        p = this.clampToArena(world, g.x, g.z, 6)
      }
      world.spawnPickup('chest', p.x, p.z, 1)
    }
  }

  private moaiBlessing(world: World): void {
    this.audio.play('chest')
    const r = world.rng.next() // seeded so daily/share runs reproduce
    if (r < 0.3) {
      healPlayer(world, world.stats.maxHp) // honors the Iron Man noHeal challenge
      world.player.shield = Math.max(world.player.shield, 60)
      this.ui.toast(t('moaiHeal'))
    } else if (r < 0.6) {
      world.gainGold(Math.round((60 + world.time) * world.stats.goldGain))
      this.ui.toast(t('moaiGold'))
    } else if (r < 0.8) {
      world.banishCharges += 2
      this.ui.toast(t('moaiBanish'))
    } else {
      this.chestQueue++
      this.ui.toast(t('moaiChest'))
      if (world.phase === 'playing') this.openChest(world)
    }
  }

  // Megabonk-style microwave: YOU choose which owned item/tome to DUPLICATE (level it up); a random same-rarity
  // item is sacrificed in exchange. Opens an interactive picker and freezes the sim until you choose or cancel.
  private microwave(world: World): void {
    type Dup = { ref: { defId: string; level: number }; kind: 'item' | 'tome'; def: { name: string; rarity: string; icon: string } }
    const dupable: Dup[] = [
      ...world.items
        .filter((it) => { const d = ITEMS[it.defId]; return d && it.level < d.maxLevel })
        .map((it) => ({ ref: it, kind: 'item' as const, def: ITEMS[it.defId] })),
      ...world.tomes
        .filter((tm) => { const d = TOMES[tm.defId]; return d && tm.level < d.maxLevel })
        .map((tm) => ({ ref: tm, kind: 'tome' as const, def: TOMES[tm.defId] })),
    ]
    if (dupable.length === 0) {
      world.gainGold(50)
      this.ui.toast(t('microwaveNone'))
      return
    }
    world.phase = 'microwave' // freeze the sim while the picker is open
    const choices = dupable.map((d) => ({
      name: tName(d.kind, d.ref.defId, d.def.name),
      rarity: d.def.rarity,
      desc: t('microwaveDupDesc', d.ref.level + 1),
      tag: `LV ${d.ref.level} → ${d.ref.level + 1}`,
      iconHtml: iconHTML(d.kind === 'item' ? itemIconSprite(d.ref.defId) : tomeIconSprite(d.ref.defId), { size: 48, fallback: d.def.icon }),
    }))
    this.ui.microwavePicker(choices, {
      pick: (i) => {
        const chosen = dupable[i]
        chosen.ref.level++ // the duplicate = another stack/level of the chosen item
        // sacrifice a random OTHER item, preferring the same rarity (Megabonk's exchange rule)
        let sacName = ''
        const others = world.items.filter((it) => it !== (chosen.ref as { defId: string; level: number }))
        if (others.length > 0) {
          const sameR = others.filter((it) => ITEMS[it.defId]?.rarity === chosen.def.rarity)
          const pool = sameR.length ? sameR : others
          const sac = pool[world.rng.int(0, pool.length - 1)]
          sacName = tName('item', sac.defId, ITEMS[sac.defId].name)
          world.items.splice(world.items.indexOf(sac), 1)
        }
        recomputeStats(world, this.metaBonuses)
        this.audio.play('evolve')
        this.renderer.addShake(0.4)
        this.flashFx('#46e0ff', 0.4)
        const dupName = tName(chosen.kind, chosen.ref.defId, chosen.def.name)
        this.ui.toast(sacName ? t('microwaveSac', dupName, sacName) : t('microwaveDup', dupName))
        this.closeMicrowave(world)
      },
      cancel: () => {
        world.gainGold(50)
        this.audio.play('select')
        this.closeMicrowave(world)
      },
    })
  }

  private closeMicrowave(world: World): void {
    world.phase = 'playing'
    this.ui.hideOverlay()
  }

  /** Microwave EXCHANGE: swap the weakest owned item for a better unowned one. */
  private microwaveExchange(world: World): boolean {
    const owned = world.items
    if (owned.length === 0) return false
    const rIdx = (id: string) => RARITY_ORDER.indexOf(ITEMS[id]?.rarity ?? 'common')
    const worst = owned.reduce((a, b) => (rIdx(a.defId) <= rIdx(b.defId) ? a : b))
    const minIdx = rIdx(worst.defId)
    const ownedIds = new Set(owned.map((o) => o.defId))
    const cands = ITEM_LIST.filter(
      (d) => !ownedIds.has(d.id) && !world.disabledItems.has(d.id) && RARITY_ORDER.indexOf(d.rarity) >= minIdx,
    )
    if (cands.length === 0) return false
    const better = cands.filter((d) => RARITY_ORDER.indexOf(d.rarity) > minIdx)
    const fromPool = better.length ? better : cands
    const pick = fromPool[world.rng.int(0, fromPool.length - 1)]
    const idx = world.items.indexOf(worst)
    world.items.splice(idx, 1)
    world.items.push({ defId: pick.id, level: 1 })
    recomputeStats(world, this.metaBonuses)
    this.audio.play('evolve')
    this.renderer.addShake(0.5)
    this.flashFx('#46e0ff', 0.4)
    this.ui.toast(t('microwaveSwap', tName('item', pick.id, pick.name)))
    return true
  }

  private chestsOpened = 0 // per-run count of regular chests opened — drives the escalating open cost (Megabonk gold sink)
  /** Escalating gold cost to OPEN a regular chest (clamped to current gold at the call site, so it never blocks). */
  private chestOpenCost(): number {
    return 25 + this.chestsOpened * 25
  }

  private chestGambleCount = 0
  private chestGambleCost(): number {
    return 60 + this.chestGambleCount * 80 // escalating per gamble this run
  }

  private openChest(world: World, source: LootSource = 'chest'): void {
    this.audio.play('chest')
    world.phase = 'chest'
    const reward = generateOptions(world, 1, source)[0]
    if (!reward) {
      world.gainGold(30)
      this.chestQueue = Math.max(0, this.chestQueue - 1)
      world.phase = this.wonPending ? 'won' : 'playing'
      return
    }
    this.showChestReveal(world, reward)
  }

  /** Pick a candidate strictly rarer than `currentRarity` (for the gamble). Chest-sourced, so it honors sourceAllows —
   *  a gamble can only re-roll into another ITEM (never a weapon/tome, which are draft-only). */
  private rollBetterReward(world: World, currentRarity: Rarity): UpgradeOption | null {
    const minIdx = RARITY_ORDER.indexOf(currentRarity) + 1
    const cands = buildCandidates(world).filter((o) => sourceAllows(o, 'chest') && RARITY_ORDER.indexOf(o.rarity) >= minIdx)
    if (cands.length === 0) return null
    return cands[world.rng.int(0, cands.length - 1)]
  }

  /** Does a strictly-rarer non-evolve candidate exist? Gates the gamble button WITHOUT consuming RNG, so we
   *  never charge gold for a gamble that can only re-show the identical reward. */
  private hasBetterReward(world: World, currentRarity: Rarity): boolean {
    const minIdx = RARITY_ORDER.indexOf(currentRarity) + 1
    return buildCandidates(world).some((o) => sourceAllows(o, 'chest') && RARITY_ORDER.indexOf(o.rarity) >= minIdx)
  }

  private showChestReveal(world: World, reward: UpgradeOption): void {
    const cost = this.chestGambleCost()
    const canGamble = world.gold >= cost && RARITY_ORDER.indexOf(reward.rarity) < RARITY_ORDER.length - 1 && this.hasBetterReward(world, reward.rarity)
    this.ui.chestReveal(
      reward,
      {
        take: () => {
          reward.apply(world)
          recomputeStats(world, this.metaBonuses)
          if (reward.kind === 'evolve') {
            this.audio.play('evolve')
            this.renderer.addShake(1)
            this.flashFx('#ffd23d', 0.5)
          } else {
            this.audio.play('levelup')
          }
          this.chestQueue = Math.max(0, this.chestQueue - 1)
          if (this.chestQueue > 0) {
            // keep the boss source for a queued boss chest (and keep bossChestQueue in sync) — mirrors the deferred drain
            if (this.bossChestQueue > 0) { this.bossChestQueue--; this.openChest(world, 'boss') }
            else this.openChest(world)
          } else {
            this.ui.hideOverlay()
            world.phase = this.wonPending ? 'won' : 'playing' // resume, or fall through to the victory screen
          }
        },
        gamble: () => {
          if (world.gold < cost) return
          const better = this.rollBetterReward(world, reward.rarity)
          if (!better) return // nothing strictly rarer exists — don't charge for an identical re-roll
          world.gold -= cost
          this.chestGambleCount++
          this.audio.play('select')
          this.showChestReveal(world, better) // re-spin the reveal at a higher rarity floor
        },
      },
      { cost, canGamble },
    )
  }

  private showVictory(world: World): void {
    this.victoryShown = true
    this.audio.play('evolve')
    this.renderer.addShake(1)
    this.flashFx('#1fe3d0', 0.5)
    this.ui.victoryChoice(world, {
      extract: () => {
        this.app = 'menu'
        this.ui.hideHud()
        this.endRun(true)
      },
      endless: () => {
        world.objective = 'endless'
        world.endless = true
        world.phase = 'playing'
        this.victoryShown = false
        this.ui.hideOverlay()
        this.audio.play('select')
        this.ui.toast(t('endlessToast'))
      },
    })
  }

  /** Drop any solid obstacle whose body engulfs a chest, breakable, or landmark shrine (placed earlier at the
   *  same POIs) — otherwise the player is pushed out before reaching the collect/trigger radius and the loot /
   *  shrine is permanently unreachable. Obstacles are decorative cover; removing the odd overlapping piece is safe. */
  private clearSealedObstacles(world: World): void {
    const M = 0.8 // margin beyond the two bodies' radii (≈ player radius) so a small gap to approach remains
    const sealedBy = (ox: number, oz: number, orad: number): boolean => {
      for (const pk of world.pickups) if (pk.kind === 'chest' && Math.hypot(ox - pk.x, oz - pk.z) < orad + (pk.radius ?? 0.8) + M) return true
      for (const b of world.breakables) if (Math.hypot(ox - b.x, oz - b.z) < orad + (b.radius ?? 0.8) + M) return true
      for (const s of world.shrines) if (Math.hypot(ox - s.x, oz - s.z) < orad + 0.6 + M) return true
      return false
    }
    world.obstacles = world.obstacles.filter((o) => !sealedBy(o.x, o.z, o.radius))
  }

  private scatterObstacles(world: World, pois: { x: number; z: number }[]): void {
    const byBiome: Record<string, string[]> = {
      meadow: ['prop:moonrock', 'prop:moonrock', 'prop:crater'],
      dunes: ['prop:redrock', 'prop:redrock', 'prop:crater'],
      graveyard: ['prop:debris', 'prop:pillar', 'prop:crate'],
      rift: ['prop:crystal', 'prop:pillar', 'prop:crystal'],
    }
    const list = byBiome[world.map.id] ?? ['prop:moonrock']
    const pushOb = (x: number, z: number) => {
      let p = this.clampToArena(world, x, z, 6)
      // cover sits on standable ground, not pasted onto a cliff face
      if (world.terrainAmp > 0) {
        const g = gentleSpot(world.rng, world.terrainSeed, world.terrainAmp, p.x, p.z, CLIFF_SLOPE, 4, 4, world.terrainCfg)
        p = this.clampToArena(world, g.x, g.z, 6)
      }
      const scale = 2.6 + world.rng.next() * 1.6
      const sprite = list[world.rng.int(0, list.length - 1)]
      world.obstacles.push({
        x: p.x,
        z: p.z,
        radius: bodyRadius(sprite, scale), // CF6: footprint matches the drawn silhouette (thin vs chunky)
        sprite,
        scale,
        blocksShots: true, // solid cover — stops shots
      })
    }
    // multi-tile STRUCTURES: seeded ruins composed from existing cover sprites, planted on
    // flat plateaus so they read as built landmarks you route to/around (always with a gap —
    // never a sealed wall; the cliff-block already shapes movement).
    const stamp = (cx: number, cz: number) => {
      let c = this.clampToArena(world, cx, cz, 8)
      if (world.terrainAmp > 0) {
        const pl = plateauSpot(world.rng, world.terrainSeed, world.terrainAmp, c.x, c.z, 5, world.terrainCfg)
        c = this.clampToArena(world, pl.x, pl.z, 8)
      }
      // a seeded multi-piece ruin (ring / pad / cluster) composed from cover sprites
      for (const off of structureOffsets(world.rng)) pushOb(c.x + off.x, c.z + off.z)
    }
    const nStruct = Math.min(pois.length, 1 + world.rng.int(0, 1)) // 1-2 structures per run
    for (let i = 0; i < nStruct; i++) stamp(pois[i].x, pois[i].z)

    // one big LANDMARK monument near (not on) the centre — a navigation anchor visible from afar,
    // a tall biome-specific piece ringed by supporting pillars with a walk-in gap
    {
      const lmByBiome: Record<string, string> = { meadow: 'prop:antenna', dunes: 'prop:pillar', graveyard: 'prop:pillar', rift: 'prop:crystal' }
      const la = world.rng.next() * Math.PI * 2
      const ld = 14 + world.rng.next() * 6 // 14-20 from centre (off the flat spawn pad)
      let lc = this.clampToArena(world, Math.cos(la) * ld, Math.sin(la) * ld, 8)
      if (world.terrainAmp > 0) {
        const pl = plateauSpot(world.rng, world.terrainSeed, world.terrainAmp, lc.x, lc.z, 5, world.terrainCfg)
        const g = gentleSpot(world.rng, world.terrainSeed, world.terrainAmp, pl.x, pl.z, CLIFF_SLOPE * 0.7, 6, 4, world.terrainCfg)
        lc = this.clampToArena(world, g.x, g.z, 8)
      }
      const bigScale = 5.5 + world.rng.next() * 1.5
      const lmSprite = lmByBiome[world.map.id] ?? 'prop:pillar'
      world.obstacles.push({
        x: lc.x,
        z: lc.z,
        radius: bodyRadius(lmSprite, bigScale), // CF6: a tall antenna mast gets a narrow base, a pillar a wide one
        sprite: lmSprite,
        scale: bigScale,
        blocksShots: true,
      })
      for (const off of landmarkOffsets(world.rng)) pushOb(lc.x + off.x, lc.z + off.z)
    }

    // short walls/arcs near some POIs form chokepoints with an open lane
    for (const poi of pois) {
      if (world.rng.next() < 0.5) continue
      const baseA = world.rng.next() * Math.PI * 2 + 0.6 // offset leaves a gap
      const arcR = 5 + world.rng.next() * 2
      const segs = 2 + world.rng.int(0, 2) // 2-4 blocks
      for (let s = 0; s < segs; s++) {
        const a = baseA + (s / Math.max(1, segs)) * 1.4
        pushOb(poi.x + Math.cos(a) * arcR, poi.z + Math.sin(a) * arcR)
      }
    }
    // top up with a few solitary cover rocks across the arena (auto-shrinks as structures fill it)
    const r = world.map.radius - 8
    const remaining = Math.max(4, 18 - world.obstacles.length)
    for (let i = 0; i < remaining; i++) {
      const a = world.rng.next() * Math.PI * 2
      const dist = 11 + world.rng.next() * Math.max(2, r - 11)
      pushOb(Math.cos(a) * dist, Math.sin(a) * dist)
    }
  }

  private endRun(victory: boolean): void {
    const world = this.world!
    const goldEarned = world.gold // unspent leftover at extraction → banked (and shown on the end screen)
    this.meta.bank += goldEarned
    this.meta.totalGoldEarned += world.goldEarned // GROSS earned this run, so spending no longer erases "earn N gold" progress
    this.meta.totalRuns++
    this.meta.totalKills += world.kills
    this.meta.bossesKilled += world.bossesKilled
    this.meta.bestTime = Math.max(this.meta.bestTime, world.time)
    this.meta.bestLevel = Math.max(this.meta.bestLevel, world.player.level)

    const unlocks = this.checkUnlocks(world, victory)
    const newAch = evaluateAchievements(this.meta, {
      time: world.time,
      level: world.player.level,
      kills: world.kills,
      bosses: world.bossesKilled,
      victory,
      difficultyId: this.selectedDifficulty,
      mapId: world.map.id,
    })
    for (const a of newAch) {
      const reward = a.reward ?? 50
      this.meta.bank += reward // granted gold goes to the bank, but is NOT counted as "earned through play"
      this.ui.toast(`${t('achToast', tName('ach', a.id, a.name))} +${reward} ${t('gold')}`)
    }
    // challenge completion: one-time gold reward on victory with a challenge active
    if (victory && world.challenge && !this.meta.challengesCompleted.includes(world.challenge)) {
      this.meta.challengesCompleted.push(world.challenge)
      const cr = 250
      this.meta.bank += cr // granted gold goes to the bank, but is NOT counted as "earned through play"
      this.ui.toast(`${t('challengeDone')} +${cr} ${t('gold')}`)
    }
    saveMeta(this.meta)
    this.audio.stopAmbience()
    this.audio.play(victory ? 'evolve' : 'death')

    this.ui.endScreen(victory, world, goldEarned, unlocks, {
      again: () => {
        this.audio.play('select')
        this.selectedChallenge = '' // a fresh re-run is a normal run (re-enter Challenges to opt back in)
        this.toCharSelect()
      },
      menu: () => this.toMenu(),
    })
  }

  private checkUnlocks(world: World, victory: boolean): string[] {
    const out: string[] = []
    const tryChar = (id: string, cond: boolean) => {
      if (cond && !this.meta.unlockedCharacters.includes(id)) {
        this.meta.unlockedCharacters.push(id)
        out.push(tName('char', id, CHARACTERS[id].name)) // localize at the source — the toast/recap chrome is translated but the {0} payload was raw EN
      }
    }
    tryChar('witch', world.player.level >= 15)
    tryChar('frost', world.time >= 300)
    tryChar('gambler', this.meta.totalGoldEarned >= 1000)
    tryChar('vampire', this.meta.bossesKilled >= 1)
    tryChar('berserker', world.player.level >= 20)
    tryChar('engineer', this.meta.totalGoldEarned >= 2500)
    tryChar('monk', world.time >= 480)
    tryChar('gunner', this.meta.totalKills >= 3000)

    if (victory) {
      if (!this.meta.clearedMaps.includes(world.map.id)) this.meta.clearedMaps.push(world.map.id)
      this.meta.bestTier[world.map.id] = Math.max(this.meta.bestTier[world.map.id] ?? 0, world.tier)
      this.meta.bestCurse[world.map.id] = Math.max(this.meta.bestCurse[world.map.id] ?? 0, world.curse)
      const idx = MAP_LIST.findIndex((m) => m.id === world.map.id)
      const next = MAP_LIST[idx + 1]
      if (next && !this.meta.unlockedMaps.includes(next.id)) {
        this.meta.unlockedMaps.push(next.id)
        out.push(tName('map', next.id, next.name)) // map names DIFFER EN/IT — raw push showed English in the IT unlock toast/recap
      }
    }
    // newer heroes — wire their unlocks (one gated behind the curse ladder)
    const maxCurse = Math.max(0, ...Object.values(this.meta.bestCurse))
    tryChar('juggernaut', world.time >= 600)
    tryChar('glasscannon', world.highestHit >= 200)
    tryChar('swarm', world.weapons.length >= MAX_WEAPONS)
    tryChar('sustain', this.meta.totalGoldEarned >= 5000)
    tryChar('executioner', this.meta.bossesKilled >= 5 || maxCurse >= 4)
    // v11 heroes
    tryChar('marksman', world.highestHit >= 500)
    tryChar('cinder', this.meta.totalGoldEarned >= 8000)
    tryChar('bastion', world.time >= 720)
    // v12 heroes (parity: roster to 21)
    tryChar('dicer', world.player.level >= 25)
    tryChar('forager', this.meta.totalGoldEarned >= 12000)
    return out
  }

  // ---- main loop ----
  private loop = (now: number) => {
    requestAnimationFrame(this.loop)
    const dt = this.last ? Math.min(0.05, (now - this.last) / 1000) : 0
    this.last = now
    if (dt > 0) this.fps = this.fps * 0.92 + (1 / dt) * 0.08

    this.input.pollGamepads(dt)
    // gamepad navigation of any open card/menu overlay (level-up, shop, menus…)
    if (this.input.gamepadActive && this.ui.overlayOpen()) this.padNavigateOverlay()

    if (this.app === 'run' && this.world) {
      this.tickRun(dt)
    }

    this.renderer.render()
    this.ui.setPadActive(this.input.gamepadActive)
    this.input.endFrame()
  }

  /** Drive UI focus from the pad while an overlay is open. */
  private padNavigateOverlay(): void {
    const dir = this.input.padNav()
    if (dir) this.ui.moveFocus(dir)
    if (this.input.padPressed(PAD.SOUTH)) this.ui.activateFocus()
    if (this.input.padPressed(PAD.EAST)) this.ui.navBack()
  }

  private tickRun(dt: number): void {
    const world = this.world!

    // pause toggle (Esc or Start). When paused/shop, the pad navigates the overlay instead.
    if (this.input.pressed('Escape') || (this.input.padPressed(PAD.START) && world.phase !== 'levelup')) {
      if (world.phase === 'playing') {
        world.phase = 'paused'
        this.showPause(world)
      } else if (world.phase === 'paused') {
        world.phase = 'playing'
        this.ui.hideOverlay()
      } else if (world.phase === 'shop') {
        this.leaveShop(world)
      }
    }

    // FEAT-C: free camera — Q/E keys + right stick rotate the orbit yaw
    const yawRate = 2.4
    if (this.input.down('KeyQ')) this.renderer.rotateYaw(yawRate * dt)
    if (this.input.down('KeyE')) this.renderer.rotateYaw(-yawRate * dt)
    const rsx = this.input.rightStickX()
    if (rsx) this.renderer.rotateYaw(-rsx * yawRate * dt)

    // build intent + advance sim — movement is camera-RELATIVE (rotate raw input by the camera yaw)
    const mv0 = this.input.moveVector()
    const mv = rotateIntent(mv0.x, mv0.z, this.renderer.camYaw)
    const intent: Intent = {
      mx: mv.x,
      mz: mv.z,
      // OR in the latched one-shots so a jump/slide tapped during a hit-stop freeze (which often runs 0 ticks) isn't
      // eaten by endFrame before any tick consumes it — slide is the only i-frame dodge, so dropping it loses a dodge.
      jump: this.input.pressed('Space') || this.input.padPressed(PAD.SOUTH) || this.pendingJump,
      slide: this.input.pressed('ShiftLeft') || this.input.pressed('ShiftRight') || this.input.padPressed(PAD.EAST) || this.pendingSlide,
    }
    // hit-stop: briefly slow the sim (6%) for impact feel on big hits/kills. Advance ONLY in whole fixed ticks —
    // feeding a sub-FIXED dt into stepWorld would route every hit-stopped frame through the framerate-dependent
    // remainder tick, so the RNG-draw count (and world.time) would diverge by framerate and break seed reproducibility.
    let ranTicks = 0
    if (world.hitStop > 0) {
      const FIXED = 1 / 60 // mirror the sim's fixed timestep so hit-stop advances in clean whole ticks
      world.hitStop = Math.max(0, world.hitStop - dt)
      this.hitStopAcc += dt * 0.06
      while (this.hitStopAcc >= FIXED) {
        this.hitStopAcc -= FIXED
        ranTicks += stepWorld(world, FIXED, intent)
      }
      // no tick ran this frame → latch jump/slide for next frame; a tick ran → it consumed them, so clear
      if (ranTicks === 0) { this.pendingJump = !!intent.jump; this.pendingSlide = !!intent.slide }
      else { this.pendingJump = false; this.pendingSlide = false }
    } else {
      ranTicks = stepWorld(world, dt, intent)
      this.pendingJump = false
      this.pendingSlide = false
    }

    this.handleEvents(world)

    // phase-driven screens
    if (world.phase === 'playing' && world.pendingLevelUps > 0 && !this.levelUpShown) {
      this.showLevelUp(world) // deferred level-up (e.g. queued behind a chest/shop)
    } else if (world.phase === 'levelup' && !this.levelUpShown) {
      this.showLevelUp(world)
    } else if (world.phase === 'playing' && this.chestQueue > 0 && !this.levelUpShown) {
      // drain a chest queued while a draft/shop/pause was open (was stranded before); keep the boss source if it was a boss chest
      if (this.bossChestQueue > 0) { this.bossChestQueue--; this.openChest(world, 'boss') }
      else this.openChest(world)
    } else if (world.phase === 'dead') {
      this.app = 'menu' // stop ticking; endRun shows overlay
      this.touch?.setActive(false)
      this.ui.hideHud()
      this.endRun(world.cleared) // count as a win if the stage boss was already beaten
    } else if (world.phase === 'won' && !this.victoryShown) {
      // open the boss reward BEFORE victory (was lost). Decrement bossChestQueue here too — the take-handler only syncs it
      // when chestQueue stays >0 after a take, so the final-boss chest (chestQueue 1→0) otherwise strands bossChestQueue=1
      // and leaks a free boss-tier reward into the next deferred chest if the player picks Endless. Mirrors the drain at :931.
      if (this.chestQueue > 0) { this.wonPending = true; if (this.bossChestQueue > 0) this.bossChestQueue--; this.openChest(world, 'boss') }
      else { this.wonPending = false; this.showVictory(world) }
    }

    // feedback: damage / kills
    if (world.player.hp < this.prevHp - 0.5) {
      this.audio.play('hurt')
      // damage shake scales with the fraction of max HP lost (informative, not constant buzz)
      const lost = (this.prevHp - world.player.hp) / Math.max(1, world.stats.maxHp)
      this.renderer.addShake(0.4 + Math.min(1.2, lost * 6))
    }
    this.prevHp = world.player.hp

    // dense per-frame SFX bed + combo crunch — gated on a tick actually running this frame. The per-frame counters
    // (shots/crits/kills/pickups/recoil) reset only inside stepWorld, so on a hit-stop frame that advances 0 ticks they
    // hold their last-tick values; without this gate the SFX + shake/punch would re-fire every render frame of the freeze.
    if (ranTicks > 0) {
      if (world.shotsThisFrame > 0) this.audio.play('shoot')
      if (world.critsThisFrame > 0) this.audio.play('crit')
      else if (world.killsThisFrame >= 6) this.audio.play('killbig')
      else if (world.killsThisFrame > 0) this.audio.play('hit')
      if (world.pickupsThisFrame > 0) this.audio.playPickup(world.combo)
      // combat crunch (render-only juice) — THROTTLED + capped into a low channel so a dense kill/crit/recoil stream is a
      // gentle PUNCTUAL rattle, never a constant screen buzz that makes the game unplayable. The per-impact freeze comes
      // from the (now rate-limited) hit-stop, not from stacking shake; no per-crit zoom-punch (that jitter was disorienting).
      if (this.meta.settings.screenShake) {
        this.crunchCd -= dt
        if (this.crunchCd <= 0 && (world.recoilThisFrame || world.critsThisFrame > 0 || world.killsThisFrame > 0)) {
          const amt = world.killsThisFrame >= 5 ? 0.16 : world.critsThisFrame > 0 ? 0.1 : world.recoilThisFrame ? 0.09 : 0.07
          this.renderer.addShake(amt, 0.28) // capped channel: peak offset stays a light rattle, well below a hurt/boss slam
          this.crunchCd = 0.08
        }
      }
    }
    if (world.combo > this.prevCombo) {
      for (const m of [25, 50, 100, 200]) {
        if (this.prevCombo < m && world.combo >= m) world.floatText(world.player.x, world.player.z, `${m} COMBO!`, 0xffd23d, true)
      }
    }
    this.prevCombo = world.combo
    this.prevKills = world.kills

    // mid-run unlocks: pop the "UNLOCKED!" toast the instant a run-scoped threshold is crossed (level/time/big-hit/
    // weapon-count) instead of only at endRun — the Megabonk "one more minute" pull. checkUnlocks is idempotent.
    if (world.time >= this.unlockCheckAt) {
      this.unlockCheckAt = world.time + 1
      const newly = this.checkUnlocks(world, false)
      if (newly.length) {
        saveMeta(this.meta)
        for (const name of newly) this.ui.toast(t('unlockToast', name))
      }
    }

    // render sync + hud (hold the camera/shake during hit-stop by passing the slowed step)
    this.audio.updateMusic(dt, world.difficulty + world.time / 120, world.time, world.enemies.length, world.enemies.some((e) => e.alive && e.isBoss))
    this.renderer.sync(world, world.hitStop > 0 ? dt * 0.06 : dt, this.meta.settings.screenShake)
    this.ui.updateHud(world, this.fps, this.renderer.camYaw)
    this.ui.syncFloaters(world, this.renderer, this.meta.settings.damageNumbers !== false)
    this.ui.syncEdgeIndicators(world, this.renderer)
  }

  /** Render the pause overlay. Extracted so toggleShake can re-render with a fresh, working handler. */
  private showPause(world: World): void {
    this.ui.pause(this.meta, world, {
      resume: () => {
        world.phase = 'playing'
        this.ui.hideOverlay()
      },
      quit: () => {
        this.ui.hideOverlay()
        this.toMenu()
      },
      toggleShake: () => {
        this.meta.settings.screenShake = !this.meta.settings.screenShake
        saveMeta(this.meta)
        this.showPause(world) // re-render so the toggle keeps working on repeated clicks
      },
    })
  }

  /** Full-screen flash, attenuated when Screen Shake (the motion/reduce-motion toggle) is OFF so the harsh
   *  near-white nuke slam etc. don't fire for motion-/photosensitive players (a faint cue is still shown). */
  private flashFx(color: string, strength: number): void {
    this.ui.flashScreen(color, this.meta.settings.screenShake ? strength : Math.min(strength, 0.15))
  }

  private handleEvents(world: World): void {
    if (world.events.length === 0) return
    for (const ev of world.events) {
      switch (ev) {
        case 'boss': {
          this.audio.play('boss')
          const boss = [...world.enemies].reverse().find((e) => e.isBoss && e.alive)
          const nm = boss ? tName('boss', boss.defId, ENEMIES[boss.defId]?.name ?? '') : t('bossIncoming')
          this.ui.bossIntro(nm, boss ? bossTitle(boss.defId) : '') // cinematic name-card + epithet

          world.hitStop = Math.max(world.hitStop, 0.2)
          this.renderer.addShake(1.2)
          this.renderer.punch(3)
          this.flashFx('#ff3a2a', 0.45)
          break
        }
        case 'boss-down':
          this.ui.toast(t('bossSlain'))
          this.renderer.addShake(0.8)
          // boss-exclusive reward: one big unique (rarest available), revealed as a chest
          this.chestQueue++
          if (world.phase === 'playing') this.openChest(world, 'boss')
          else this.bossChestQueue++ // deferred behind a draft/shop — remember it's a BOSS chest so the late drain keeps the rarest-reward source
          break
        case 'elite':
          this.ui.toast(t('eliteAppeared'))
          break
        case 'miniboss': {
          this.audio.play('boss')
          const mb = [...world.enemies].reverse().find((e) => e.isMiniBoss && e.alive)
          const nm = mb ? `${t('miniBossIncoming')} · ${tName('enemy', mb.defId, ENEMIES[mb.defId]?.name ?? '')}` : t('miniBossIncoming')
          this.ui.bossIntro(nm)
          this.ui.toast(t('miniBossToast'))
          this.renderer.addShake(0.7)
          this.renderer.punch(1.8)
          break
        }
        case 'miniboss-down':
          this.ui.toast(t('miniBossDown'))
          this.renderer.addShake(0.5)
          break
        case 'surge':
          this.audio.play('boss')
          this.ui.toast(t('surgeIncoming'))
          this.renderer.addShake(0.4)
          break
        case 'chest': {
          // Megabonk economy lever: opening a regular chest costs escalating gold (a sink). Clamped to current gold so
          // it never blocks the reward — you just pay what you can; the cost rises with each chest opened this run.
          const cost = Math.min(world.gold, this.chestOpenCost())
          if (cost > 0) {
            world.gold -= cost
            world.floatText(world.player.x, world.player.z, `-${cost}`, 0xffd23d, true)
          }
          this.chestsOpened++
          this.chestQueue++
          if (world.phase === 'playing') this.openChest(world)
          break
        }
        case 'revive':
          this.audio.play('evolve')
          this.ui.toast(t('revived'))
          this.renderer.addShake(1)
          this.renderer.punch(2)
          break
        case 'nuke':
          // screen-clear bomb: white flash, heavy slam, zoom jolt
          this.audio.play('nuke')
          this.flashFx('#ffffff', 0.85)
          this.renderer.addShake(1.6)
          this.renderer.punch(3)
          world.hitStop = Math.max(world.hitStop, 0.12)
          break
        case 'magnet':
          this.audio.play('magnet')
          break
        case 'vendor':
          this.ui.toast(t('vendorAppeared'))
          break
        case 'portal':
          this.audio.play('chest')
          this.ui.toast(t('portalAppeared'))
          this.renderer.addShake(0.6)
          this.renderer.punch(1.5)
          break
        case 'portal-warning':
          this.audio.play('boss')
          this.ui.toast(t('portalWarning'))
          this.renderer.addShake(0.5)
          this.flashFx('#b44cff', 0.35)
          break
        case 'stage-clear':
          this.audio.play('levelup')
          this.ui.toast(t('stageClear'))
          this.flashFx('#5fe6ff', 0.5)
          this.renderer.punch(2)
          break
        case 'shop':
          this.openShop(world)
          break
        case 'finalboss':
          // name-card + punch already fired on the paired 'boss' event; just add the banner
          this.ui.toast(t('finalbossToast'))
          break
        case 'shrine:magnet':
          this.ui.toast(t('shrineMagnet'))
          break
        case 'shrine:gold':
          this.ui.toast(t('shrineGold'))
          break
        case 'shrine:heal':
          this.ui.toast(t('shrineHeal'))
          break
        case 'shrine:treasure':
          this.audio.play('chest')
          this.ui.toast(t('shrineTreasure'))
          break
        case 'shrine:battle':
          this.audio.play('boss')
          this.ui.toast(t('shrineBattle'))
          this.renderer.addShake(0.6)
          break
        case 'shrine:curse':
          this.audio.play('boss')
          this.ui.toast(t('shrineCurse'))
          this.renderer.addShake(0.5)
          break
        case 'shrine:charge':
          this.audio.play('evolve')
          this.ui.toast(t('shrineCharge'))
          this.renderer.addShake(0.5)
          this.flashFx('#66e0ff', 0.4)
          break
        case 'moai':
          this.moaiBlessing(world)
          break
        case 'microwave':
          this.microwave(world)
          break
        case 'levelup':
          // world feedback: an expanding ring pulses out from the player on level-up
          this.renderer.triggerBurst(world.player.x, world.player.z, 'levelup')
          break
      }
    }
    world.events.length = 0
  }

  // ---- in-run shop ----
  private priceFor(rarity: string, level: number): number {
    const base: Record<string, number> = { common: 25, uncommon: 50, rare: 90, epic: 150, legendary: 260 }
    return Math.round((base[rarity] ?? 40) * (1 + level * 0.03))
  }

  private openShop(world: World): void {
    world.phase = 'shop'
    this.audio.play('chest')
    this.shopRerollCount = 0
    // drop net-zero/loss 'gold'/'heal' fallback padding — a paid "Coin Stash" (+50 gold for ~50+) is a trap, and
    // the vendor already appends its own fixed Hearty Meal staple below
    const opts = generateOptions(world, 4, 'vendor').filter((o) => o.kind !== 'gold' && o.kind !== 'heal')
    this.shopOffers = opts.map((o) => ({
      icon: o.icon,
      kind: o.kind,
      id: o.id,
      name: o.name,
      rarity: o.rarity,
      desc: o.desc,
      price: this.priceFor(o.rarity, world.player.level),
      sold: false,
      apply: o.apply,
    }))
    this.shopOffers.push({
      icon: '',
      kind: 'heal',
      name: t('heartyMeal'),
      rarity: 'common',
      desc: t('heartyMealDesc'),
      price: 35,
      sold: false,
      fixed: true,
      apply: (w) => {
        healPlayer(w, w.stats.maxHp * 0.5) // honors the Iron Man noHeal challenge
      },
    })
    this.shopOffers.push({
      icon: '',
      kind: 'banish',
      name: t('banishCharge'),
      rarity: 'uncommon',
      desc: t('banishChargeDesc'),
      price: 55,
      sold: false,
      fixed: true,
      apply: (w) => {
        w.banishCharges++
      },
    })
    this.renderShop(world)
  }

  private shopRerollCost(): number {
    return 30 + 30 * this.shopRerollCount
  }

  private renderShop(world: World): void {
    const cost = this.shopRerollCost()
    this.ui.shopInRun(
      this.shopOffers,
      world.gold,
      {
        buy: (i) => this.buyShop(world, i),
        leave: () => this.leaveShop(world),
        reroll: () => this.doShopReroll(world),
        lock: (i) => this.doShopLock(world, i),
      },
      { cost, canReroll: world.gold >= cost },
      { weaponFull: world.weapons.length >= MAX_WEAPONS, itemFull: world.items.length >= MAX_ITEMS, tomeFull: world.tomes.length >= MAX_TOMES },
    )
  }

  private doShopReroll(world: World): void {
    const cost = this.shopRerollCost()
    if (world.gold < cost) return
    world.gold -= cost
    this.shopRerollCount++
    this.audio.play('select')
    // exclude offers still on the board (locked/fixed) so a reroll can't surface a duplicate card
    const retained = new Set(this.shopOffers.filter((o) => o.fixed || o.locked).map((o) => `${o.kind}:${o.id}`))
    const fresh = generateOptions(world, 8, 'vendor').filter((n) => !retained.has(`${n.kind}:${n.id}`))
    let fi = 0
    this.shopOffers = this.shopOffers.map((o) => {
      if (o.fixed || o.locked) return o
      const n = fresh[fi++]
      if (!n) return o
      return { icon: n.icon, kind: n.kind, id: n.id, name: n.name, rarity: n.rarity, desc: n.desc, price: this.priceFor(n.rarity, world.player.level), sold: false, apply: n.apply }
    })
    this.renderShop(world)
  }

  private doShopLock(world: World, i: number): void {
    const o = this.shopOffers[i]
    if (!o || o.fixed) return
    o.locked = !o.locked
    this.audio.play('select')
    this.renderShop(world)
  }

  private buyShop(world: World, i: number): void {
    const offer = this.shopOffers[i]
    if (!offer || offer.sold || world.gold < offer.price) return
    // respect loadout caps on a multi-buy (apply() hard-guards too, but never charge for a no-op)
    if (offer.kind === 'weapon-new' && world.weapons.length >= MAX_WEAPONS) return
    if (offer.kind === 'item-new' && world.items.length >= MAX_ITEMS) return
    if (offer.kind === 'tome-new' && world.tomes.length >= MAX_TOMES) return
    world.gold -= offer.price
    offer.apply(world)
    recomputeStats(world, this.metaBonuses)
    offer.sold = true
    this.audio.play('levelup')
    this.renderShop(world)
  }

  private leaveShop(world: World): void {
    this.ui.hideOverlay()
    world.phase = 'playing'
    this.audio.play('select')
  }

  private rerollCost(): number {
    return 10 + 8 * this.rerollCount
  }

  private showLevelUp(world: World): void {
    this.levelUpShown = true
    world.phase = 'levelup' // freeze the sim while the draft is open
    this.rerollCount = 0
    this.audio.play('levelup')
    this.currentOptions = generateOptions(world, 3)
    this.renderLevelUp(world)
  }

  private renderLevelUp(world: World): void {
    const cost = this.rerollCost()
    this.ui.levelUp(
      this.currentOptions,
      world.pendingLevelUps,
      {
        rerollCost: cost,
        canReroll: world.gold >= cost,
        banishCharges: world.banishCharges,
        lockedKey: world.lockedKey,
        gold: world.gold,
      },
      {
        pick: (o) => this.pickUpgrade(world, o),
        reroll: () => this.doReroll(world),
        banish: (o) => this.doBanish(world, o),
        lock: (o) => this.doLock(world, o),
        skip: () => this.doSkip(world),
      },
    )
  }

  private doSkip(world: World): void {
    // skip the upgrade for a small gold + xp consolation, then advance the queue
    world.gainGold(25)
    recomputeStats(world, this.metaBonuses) // apply any per-level character scaling for the level just gained (skip still levels you)
    this.audio.play('select')
    world.pendingLevelUps = Math.max(0, world.pendingLevelUps - 1)
    if (world.pendingLevelUps > 0) {
      this.rerollCount = 0
      this.currentOptions = generateOptions(world, 3)
      this.renderLevelUp(world)
    } else {
      this.levelUpShown = false
      this.ui.hideOverlay()
      world.phase = 'playing'
    }
  }

  private doReroll(world: World): void {
    const cost = this.rerollCost()
    if (world.gold < cost) return
    world.gold -= cost
    this.rerollCount++
    this.currentOptions = generateOptions(world, 3)
    this.audio.play('select')
    this.renderLevelUp(world)
  }

  private doBanish(world: World, opt: UpgradeOption): void {
    if (world.banishCharges <= 0 || !isBanishable(opt)) return
    world.banishCharges--
    world.banished.add(opt.id)
    if (world.lockedKey === optionKey(opt)) world.lockedKey = null
    this.currentOptions = generateOptions(world, 3)
    this.audio.play('select')
    this.renderLevelUp(world)
  }

  private doLock(world: World, opt: UpgradeOption): void {
    const k = optionKey(opt)
    world.lockedKey = world.lockedKey === k ? null : k
    this.audio.play('select')
    this.renderLevelUp(world)
  }

  private pickUpgrade(world: World, opt: UpgradeOption): void {
    opt.apply(world)
    recomputeStats(world, this.metaBonuses)
    if (world.lockedKey === optionKey(opt)) world.lockedKey = null // consumed
    if (opt.kind === 'evolve') {
      this.meta.evolvedTotal++
      saveMeta(this.meta) // persist immediately so a quit / tab-close can't lose the lifetime evolve count
      this.audio.play('evolve')
      this.renderer.addShake(1.3)
      this.renderer.triggerBurst(world.player.x, world.player.z, 'evolve')
      this.flashFx('#ffd23d', 0.6)
      this.ui.toast(t('evolvedInto', opt.name))
    } else {
      this.audio.play('select')
    }
    world.pendingLevelUps = Math.max(0, world.pendingLevelUps - 1)

    if (world.pendingLevelUps > 0) {
      this.rerollCount = 0
      this.currentOptions = generateOptions(world, 3)
      this.renderLevelUp(world)
    } else {
      this.levelUpShown = false
      this.ui.hideOverlay()
      world.phase = 'playing'
    }
  }
}

export { CHARACTER_LIST }
